import React, { useEffect, useState } from "react";
import TopNav from "../components/TopNav";
import { appsApi, annApi, usersApi, statsApi } from "../lib/api";
import {
    Plus,
    Pencil,
    Trash2,
    Megaphone,
    Save,
    X,
    UserPlus,
    ShieldCheck,
    Shield,
    Send,
    Users as UsersIcon,
    AppWindow,
    BarChart3,
    TrendingUp,
    Ghost,
} from "lucide-react";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import UsageHeatmap from "../components/UsageHeatmap";

const ICON_OPTIONS = [
    "Globe",
    "Mail",
    "MessageSquare",
    "Calendar",
    "HardDrive",
    "ShoppingCart",
    "Ticket",
    "Package",
    "Users",
    "FileText",
    "Briefcase",
    "BarChart3",
    "CreditCard",
    "Phone",
    "Settings",
    "Wrench",
    "Headphones",
    "Truck",
    "ClipboardList",
    "Shield",
];

const emptyAppForm = {
    id: null,
    name: "",
    url: "",
    icon: "Globe",
    category: "General",
    description: "",
};

const emptyUserForm = {
    id: null,
    email: "",
    first_name: "",
    last_name: "",
    is_admin: false,
    access_mode: "password", // "password" | "invite"
    password: "",
};

export default function Admin() {
    const { user: me } = useAuth();
    const [tab, setTab] = useState("users"); // users | apps | announcements | stats
    const [apps, setApps] = useState([]);
    const [ann, setAnn] = useState([]);
    const [users, setUsers] = useState([]);
    const [stats, setStats] = useState(null);
    const [statsLoading, setStatsLoading] = useState(false);
    const [appForm, setAppForm] = useState(emptyAppForm);
    const [userForm, setUserForm] = useState(emptyUserForm);
    const [annForm, setAnnForm] = useState({ title: "", body: "" });
    const [busy, setBusy] = useState(false);

    const loadAll = async () => {
        try {
            const [a, n, u] = await Promise.all([
                appsApi.list(),
                annApi.list(),
                usersApi.list(),
            ]);
            setApps(a);
            setAnn(n);
            setUsers(u);
        } catch (err) {
            toast.error("Failed to load admin data");
        }
    };

    useEffect(() => {
        loadAll();
    }, []);

    const loadStats = async () => {
        setStatsLoading(true);
        try {
            const s = await statsApi.overview();
            setStats(s);
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Failed to load stats");
        } finally {
            setStatsLoading(false);
        }
    };

    useEffect(() => {
        if (tab === "stats" && !stats) loadStats();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [tab]);

    // ---------------- Users ----------------
    const saveUser = async (e) => {
        e.preventDefault();
        if (!userForm.email) {
            toast.error("Email is required");
            return;
        }
        setBusy(true);
        try {
            if (userForm.id) {
                const payload = {
                    first_name: userForm.first_name,
                    last_name: userForm.last_name,
                    is_admin: userForm.is_admin,
                };
                if (userForm.password) payload.password = userForm.password;
                await usersApi.update(userForm.id, payload);
                toast.success("User updated");
            } else {
                const payload = {
                    email: userForm.email.trim().toLowerCase(),
                    first_name: userForm.first_name.trim(),
                    last_name: userForm.last_name.trim(),
                    is_admin: userForm.is_admin,
                };
                if (userForm.access_mode === "password") {
                    if (!userForm.password) {
                        toast.error("Password is required");
                        setBusy(false);
                        return;
                    }
                    payload.password = userForm.password;
                } else {
                    payload.send_invite = true;
                }
                await usersApi.create(payload);
                toast.success(
                    userForm.access_mode === "invite"
                        ? "User created, magic-link invite sent"
                        : "User created",
                );
            }
            setUserForm(emptyUserForm);
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const editUser = (u) => {
        setUserForm({
            id: u.id,
            email: u.email,
            first_name: u.first_name || "",
            last_name: u.last_name || "",
            is_admin: !!u.is_admin,
            access_mode: "password",
            password: "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const deleteUser = async (u) => {
        if (u.id === me.id) {
            toast.error("You can't delete your own account.");
            return;
        }
        if (!window.confirm(`Delete user "${u.email}"?`)) return;
        try {
            await usersApi.remove(u.id);
            toast.success("User deleted");
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Delete failed");
        }
    };

    const sendInvite = async (u) => {
        try {
            await usersApi.sendInvite(u.id);
            toast.success(`Magic link sent to ${u.email}`);
        } catch {
            toast.error("Failed to send invite");
        }
    };

    const toggleAdmin = async (u) => {
        if (u.id === me.id && u.is_admin) {
            toast.error("You can't remove admin from yourself.");
            return;
        }
        try {
            await usersApi.update(u.id, { is_admin: !u.is_admin });
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Update failed");
        }
    };

    // ---------------- Apps ----------------
    const saveApp = async (e) => {
        e.preventDefault();
        if (!appForm.name || !appForm.url) {
            toast.error("Name and URL are required");
            return;
        }
        setBusy(true);
        try {
            const payload = {
                name: appForm.name.trim(),
                url: appForm.url.trim(),
                icon: appForm.icon,
                category: appForm.category.trim() || "General",
                description: appForm.description.trim(),
            };
            if (appForm.id) {
                await appsApi.update(appForm.id, payload);
                toast.success("App updated");
            } else {
                await appsApi.create(payload);
                toast.success("App added");
            }
            setAppForm(emptyAppForm);
            await loadAll();
        } catch (err) {
            toast.error(err?.response?.data?.detail || "Save failed");
        } finally {
            setBusy(false);
        }
    };

    const editApp = (a) => {
        setAppForm({
            id: a.id,
            name: a.name,
            url: a.url,
            icon: a.icon || "Globe",
            category: a.category || "General",
            description: a.description || "",
        });
        window.scrollTo({ top: 0, behavior: "smooth" });
    };

    const deleteApp = async (a) => {
        if (!window.confirm(`Delete "${a.name}"?`)) return;
        try {
            await appsApi.remove(a.id);
            toast.success("Deleted");
            await loadAll();
        } catch {
            toast.error("Delete failed");
        }
    };

    // ---------------- Announcements ----------------
    const postAnnouncement = async (e) => {
        e.preventDefault();
        if (!annForm.title || !annForm.body) {
            toast.error("Title and body are required");
            return;
        }
        try {
            await annApi.create(annForm);
            setAnnForm({ title: "", body: "" });
            toast.success("Announcement posted");
            await loadAll();
        } catch {
            toast.error("Post failed");
        }
    };

    const deleteAnnouncement = async (a) => {
        if (!window.confirm(`Delete "${a.title}"?`)) return;
        try {
            await annApi.remove(a.id);
            toast.success("Deleted");
            await loadAll();
        } catch {
            toast.error("Delete failed");
        }
    };

    const tabBtn = (id, label, Icon) => (
        <button
            key={id}
            onClick={() => setTab(id)}
            className={[
                "font-display text-sm px-4 py-2 brut-border inline-flex items-center gap-2",
                tab === id
                    ? "bg-[#E8241A] text-white"
                    : "bg-white text-black hover:bg-black hover:text-white",
            ].join(" ")}
            data-testid={`admin-tab-${id}`}
        >
            <Icon size={16} strokeWidth={3} />
            {label}
        </button>
    );

    return (
        <div className="min-h-screen" data-testid="admin-page">
            <TopNav />
            <main className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-10">
                <h1 className="font-display text-4xl sm:text-5xl lg:text-6xl leading-none mb-6">
                    <span className="bg-black text-white px-2 inline-block">
                        ADMIN
                    </span>{" "}
                    Controls
                </h1>

                <div className="flex flex-wrap gap-2 mb-8">
                    {tabBtn("users", "Users", UsersIcon)}
                    {tabBtn("apps", "Apps", AppWindow)}
                    {tabBtn("announcements", "Announcements", Megaphone)}
                    {tabBtn("stats", "Analytics", BarChart3)}
                </div>

                {tab === "users" && (
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        {/* User Form */}
                        <section className="lg:col-span-2 bg-white brut-border brut-shadow p-5 sm:p-6 h-fit">
                            <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
                                {userForm.id ? (
                                    <Pencil size={20} strokeWidth={3} />
                                ) : (
                                    <UserPlus size={20} strokeWidth={3} />
                                )}
                                {userForm.id ? "Edit User" : "Add User"}
                            </h2>
                            <form onSubmit={saveUser} className="space-y-3">
                                <Field label="Email">
                                    <input
                                        type="email"
                                        value={userForm.email}
                                        disabled={!!userForm.id}
                                        onChange={(e) =>
                                            setUserForm({
                                                ...userForm,
                                                email: e.target.value,
                                            })
                                        }
                                        required
                                        placeholder="name@911cellphonebank.org"
                                        className="w-full px-3 py-2 brut-border bg-white outline-none disabled:opacity-60"
                                        data-testid="user-form-email"
                                    />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="First name">
                                        <input
                                            type="text"
                                            value={userForm.first_name}
                                            onChange={(e) =>
                                                setUserForm({
                                                    ...userForm,
                                                    first_name: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 brut-border bg-white outline-none"
                                            data-testid="user-form-first"
                                        />
                                    </Field>
                                    <Field label="Last name">
                                        <input
                                            type="text"
                                            value={userForm.last_name}
                                            onChange={(e) =>
                                                setUserForm({
                                                    ...userForm,
                                                    last_name: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 brut-border bg-white outline-none"
                                            data-testid="user-form-last"
                                        />
                                    </Field>
                                </div>

                                <label className="flex items-center gap-2 brut-border bg-[#FAFAF7] p-3 cursor-pointer">
                                    <input
                                        type="checkbox"
                                        checked={userForm.is_admin}
                                        onChange={(e) =>
                                            setUserForm({
                                                ...userForm,
                                                is_admin: e.target.checked,
                                            })
                                        }
                                        className="w-4 h-4 accent-[#E8241A]"
                                        data-testid="user-form-isadmin"
                                    />
                                    <span className="font-display text-xs tracking-widest">
                                        Make Admin
                                    </span>
                                </label>

                                {!userForm.id && (
                                    <div className="brut-border p-3 space-y-2">
                                        <span className="block font-display text-[11px] tracking-widest">
                                            Access
                                        </span>
                                        <div className="flex gap-2">
                                            <RadioPill
                                                checked={
                                                    userForm.access_mode ===
                                                    "password"
                                                }
                                                onClick={() =>
                                                    setUserForm({
                                                        ...userForm,
                                                        access_mode: "password",
                                                    })
                                                }
                                                testId="user-form-access-password"
                                            >
                                                Set password
                                            </RadioPill>
                                            <RadioPill
                                                checked={
                                                    userForm.access_mode ===
                                                    "invite"
                                                }
                                                onClick={() =>
                                                    setUserForm({
                                                        ...userForm,
                                                        access_mode: "invite",
                                                    })
                                                }
                                                testId="user-form-access-invite"
                                            >
                                                Send magic-link invite
                                            </RadioPill>
                                        </div>
                                    </div>
                                )}

                                {(userForm.id ||
                                    userForm.access_mode === "password") && (
                                    <Field
                                        label={
                                            userForm.id
                                                ? "New password (optional)"
                                                : "Password"
                                        }
                                    >
                                        <input
                                            type="password"
                                            value={userForm.password}
                                            onChange={(e) =>
                                                setUserForm({
                                                    ...userForm,
                                                    password: e.target.value,
                                                })
                                            }
                                            placeholder="min 8 chars, 1 letter & 1 number"
                                            className="w-full px-3 py-2 brut-border bg-white outline-none"
                                            data-testid="user-form-password"
                                        />
                                    </Field>
                                )}

                                <div className="flex items-center gap-3 pt-1">
                                    <button
                                        type="submit"
                                        disabled={busy}
                                        className="flex-1 bg-[#E8241A] text-white font-display text-base px-4 py-3 brut-border brut-shadow-sm brut-press inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                        data-testid="user-form-submit"
                                    >
                                        <Save size={16} strokeWidth={3} />
                                        {userForm.id ? "Save" : "Create"}
                                    </button>
                                    {userForm.id && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setUserForm(emptyUserForm)
                                            }
                                            className="font-display text-base px-4 py-3 brut-border bg-white brut-press inline-flex items-center gap-2"
                                            data-testid="user-form-cancel"
                                        >
                                            <X size={16} strokeWidth={3} />
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </section>

                        {/* Users list */}
                        <section className="lg:col-span-3 bg-white brut-border brut-shadow">
                            <div className="bg-black text-white px-4 py-2 font-display">
                                Users ({users.length})
                            </div>
                            <ul className="divide-y-[3px] divide-black">
                                {users.map((u) => (
                                    <li
                                        key={u.id}
                                        className="flex items-center justify-between gap-3 px-4 py-3"
                                        data-testid={`admin-user-row-${u.id}`}
                                    >
                                        <div className="min-w-0 flex items-center gap-3">
                                            <div className="bg-black text-white w-10 h-10 flex items-center justify-center font-display text-lg brut-border shrink-0">
                                                {(u.first_name ||
                                                    u.email)[0].toUpperCase()}
                                            </div>
                                            <div className="min-w-0">
                                                <div className="font-display text-sm leading-tight break-all">
                                                    {u.email}
                                                </div>
                                                <div className="text-xs opacity-70">
                                                    {[
                                                        u.first_name,
                                                        u.last_name,
                                                    ]
                                                        .filter(Boolean)
                                                        .join(" ") || "—"}
                                                </div>
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                onClick={() => toggleAdmin(u)}
                                                className={[
                                                    "px-2 py-2 brut-border brut-press inline-flex items-center gap-1 font-display text-[11px] tracking-widest",
                                                    u.is_admin
                                                        ? "bg-[#E8241A] text-white"
                                                        : "bg-white text-black",
                                                ].join(" ")}
                                                data-testid={`admin-user-toggle-${u.id}`}
                                                title={
                                                    u.is_admin
                                                        ? "Admin — click to demote"
                                                        : "Employee — click to promote"
                                                }
                                            >
                                                {u.is_admin ? (
                                                    <ShieldCheck
                                                        size={14}
                                                        strokeWidth={3}
                                                    />
                                                ) : (
                                                    <Shield
                                                        size={14}
                                                        strokeWidth={3}
                                                    />
                                                )}
                                                <span className="hidden sm:inline">
                                                    {u.is_admin
                                                        ? "Admin"
                                                        : "User"}
                                                </span>
                                            </button>
                                            <button
                                                onClick={() => sendInvite(u)}
                                                className="px-2 py-2 brut-border bg-white brut-press"
                                                data-testid={`admin-user-invite-${u.id}`}
                                                title="Send magic-link sign-in"
                                                aria-label="Send invite"
                                            >
                                                <Send
                                                    size={14}
                                                    strokeWidth={3}
                                                />
                                            </button>
                                            <button
                                                onClick={() => editUser(u)}
                                                className="px-2 py-2 brut-border bg-white brut-press"
                                                data-testid={`admin-user-edit-${u.id}`}
                                                aria-label="Edit user"
                                            >
                                                <Pencil
                                                    size={14}
                                                    strokeWidth={3}
                                                />
                                            </button>
                                            <button
                                                onClick={() => deleteUser(u)}
                                                disabled={u.id === me.id}
                                                className="px-2 py-2 brut-border bg-[#E8241A] text-white brut-press disabled:opacity-40 disabled:cursor-not-allowed"
                                                data-testid={`admin-user-del-${u.id}`}
                                                aria-label="Delete user"
                                            >
                                                <Trash2
                                                    size={14}
                                                    strokeWidth={3}
                                                />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                                {users.length === 0 && (
                                    <li className="p-6 font-display opacity-60">
                                        No users yet.
                                    </li>
                                )}
                            </ul>
                        </section>
                    </div>
                )}

                {tab === "apps" && (
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        <section className="lg:col-span-2 bg-white brut-border brut-shadow p-5 sm:p-6 h-fit">
                            <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
                                {appForm.id ? (
                                    <Pencil size={20} strokeWidth={3} />
                                ) : (
                                    <Plus size={20} strokeWidth={3} />
                                )}
                                {appForm.id ? "Edit App" : "Add App"}
                            </h2>
                            <form onSubmit={saveApp} className="space-y-3">
                                <Field label="Name">
                                    <input
                                        type="text"
                                        value={appForm.name}
                                        onChange={(e) =>
                                            setAppForm({
                                                ...appForm,
                                                name: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 brut-border bg-white outline-none"
                                        data-testid="app-form-name"
                                        required
                                    />
                                </Field>
                                <Field label="URL">
                                    <input
                                        type="url"
                                        value={appForm.url}
                                        onChange={(e) =>
                                            setAppForm({
                                                ...appForm,
                                                url: e.target.value,
                                            })
                                        }
                                        placeholder="https://"
                                        className="w-full px-3 py-2 brut-border bg-white outline-none"
                                        data-testid="app-form-url"
                                        required
                                    />
                                </Field>
                                <div className="grid grid-cols-2 gap-3">
                                    <Field label="Icon">
                                        <select
                                            value={appForm.icon}
                                            onChange={(e) =>
                                                setAppForm({
                                                    ...appForm,
                                                    icon: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 brut-border bg-white outline-none"
                                            data-testid="app-form-icon"
                                        >
                                            {ICON_OPTIONS.map((i) => (
                                                <option key={i} value={i}>
                                                    {i}
                                                </option>
                                            ))}
                                        </select>
                                    </Field>
                                    <Field label="Category">
                                        <input
                                            type="text"
                                            value={appForm.category}
                                            onChange={(e) =>
                                                setAppForm({
                                                    ...appForm,
                                                    category: e.target.value,
                                                })
                                            }
                                            className="w-full px-3 py-2 brut-border bg-white outline-none"
                                            data-testid="app-form-category"
                                        />
                                    </Field>
                                </div>
                                <Field label="Description (optional)">
                                    <input
                                        type="text"
                                        value={appForm.description}
                                        onChange={(e) =>
                                            setAppForm({
                                                ...appForm,
                                                description: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 brut-border bg-white outline-none"
                                        data-testid="app-form-description"
                                    />
                                </Field>
                                <div className="flex items-center gap-3 pt-1">
                                    <button
                                        type="submit"
                                        disabled={busy}
                                        className="flex-1 bg-[#E8241A] text-white font-display text-base px-4 py-3 brut-border brut-shadow-sm brut-press inline-flex items-center justify-center gap-2 disabled:opacity-60"
                                        data-testid="app-form-submit"
                                    >
                                        <Save size={16} strokeWidth={3} />
                                        {appForm.id ? "Save" : "Add"}
                                    </button>
                                    {appForm.id && (
                                        <button
                                            type="button"
                                            onClick={() =>
                                                setAppForm(emptyAppForm)
                                            }
                                            className="font-display text-base px-4 py-3 brut-border bg-white brut-press inline-flex items-center gap-2"
                                            data-testid="app-form-cancel"
                                        >
                                            <X size={16} strokeWidth={3} />
                                            Cancel
                                        </button>
                                    )}
                                </div>
                            </form>
                        </section>

                        <section className="lg:col-span-3 bg-white brut-border brut-shadow">
                            <div className="bg-black text-white px-4 py-2 font-display">
                                Apps ({apps.length})
                            </div>
                            <ul className="divide-y-[3px] divide-black">
                                {apps.map((a) => (
                                    <li
                                        key={a.id}
                                        className="flex items-center justify-between gap-3 px-4 py-3"
                                        data-testid={`admin-app-row-${a.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="font-display text-base leading-tight">
                                                {a.name}
                                            </div>
                                            <div className="text-xs opacity-70 truncate">
                                                {a.url}
                                            </div>
                                        </div>
                                        <div className="flex items-center gap-2 shrink-0">
                                            <button
                                                onClick={() => editApp(a)}
                                                className="px-3 py-2 brut-border bg-white brut-press"
                                                data-testid={`admin-app-edit-${a.id}`}
                                                aria-label={`Edit ${a.name}`}
                                            >
                                                <Pencil
                                                    size={14}
                                                    strokeWidth={3}
                                                />
                                            </button>
                                            <button
                                                onClick={() => deleteApp(a)}
                                                className="px-3 py-2 brut-border bg-[#E8241A] text-white brut-press"
                                                data-testid={`admin-app-del-${a.id}`}
                                                aria-label={`Delete ${a.name}`}
                                            >
                                                <Trash2
                                                    size={14}
                                                    strokeWidth={3}
                                                />
                                            </button>
                                        </div>
                                    </li>
                                ))}
                                {apps.length === 0 && (
                                    <li className="p-6 font-display opacity-60">
                                        No apps yet — add your first.
                                    </li>
                                )}
                            </ul>
                        </section>
                    </div>
                )}

                {tab === "announcements" && (
                    <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
                        <section className="lg:col-span-2 bg-white brut-border brut-shadow p-5 sm:p-6 h-fit">
                            <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
                                <Megaphone size={20} strokeWidth={3} /> Post
                                Announcement
                            </h2>
                            <form
                                onSubmit={postAnnouncement}
                                className="space-y-3"
                            >
                                <Field label="Title">
                                    <input
                                        type="text"
                                        value={annForm.title}
                                        onChange={(e) =>
                                            setAnnForm({
                                                ...annForm,
                                                title: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 brut-border bg-white outline-none"
                                        data-testid="ann-form-title"
                                        required
                                    />
                                </Field>
                                <Field label="Message">
                                    <textarea
                                        rows={4}
                                        value={annForm.body}
                                        onChange={(e) =>
                                            setAnnForm({
                                                ...annForm,
                                                body: e.target.value,
                                            })
                                        }
                                        className="w-full px-3 py-2 brut-border bg-white outline-none"
                                        data-testid="ann-form-body"
                                        required
                                    />
                                </Field>
                                <button
                                    type="submit"
                                    className="w-full bg-[#E8241A] text-white font-display text-base px-4 py-3 brut-border brut-shadow-sm brut-press"
                                    data-testid="ann-form-submit"
                                >
                                    Post to Everyone
                                </button>
                            </form>
                        </section>

                        <section className="lg:col-span-3 bg-white brut-border brut-shadow">
                            <div className="bg-black text-white px-4 py-2 font-display">
                                Announcements ({ann.length})
                            </div>
                            <ul className="divide-y-[3px] divide-black">
                                {ann.map((a) => (
                                    <li
                                        key={a.id}
                                        className="flex items-start justify-between gap-3 px-4 py-3"
                                        data-testid={`admin-ann-row-${a.id}`}
                                    >
                                        <div className="min-w-0">
                                            <div className="font-display text-base leading-tight">
                                                {a.title}
                                            </div>
                                            <div className="text-xs opacity-70 mt-1 whitespace-pre-wrap">
                                                {a.body}
                                            </div>
                                            <div className="text-[10px] uppercase tracking-widest opacity-60 mt-1">
                                                {new Date(
                                                    a.created_at,
                                                ).toLocaleString()}{" "}
                                                • {a.author_email}
                                            </div>
                                        </div>
                                        <button
                                            onClick={() =>
                                                deleteAnnouncement(a)
                                            }
                                            className="px-3 py-2 brut-border bg-[#E8241A] text-white brut-press shrink-0"
                                            data-testid={`admin-ann-del-${a.id}`}
                                            aria-label={`Delete ${a.title}`}
                                        >
                                            <Trash2
                                                size={14}
                                                strokeWidth={3}
                                            />
                                        </button>
                                    </li>
                                ))}
                                {ann.length === 0 && (
                                    <li className="p-6 font-display opacity-60">
                                        No announcements yet.
                                    </li>
                                )}
                            </ul>
                        </section>
                    </div>
                )}

                {tab === "stats" && (
                    <div
                        className="space-y-8"
                        data-testid="admin-stats-panel"
                    >
                        {statsLoading || !stats ? (
                            <div className="font-display text-lg opacity-60">
                                LOADING ANALYTICS…
                            </div>
                        ) : (
                            <>
                                {/* Totals */}
                                <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
                                    <StatCard
                                        label="Launches · 30d"
                                        value={stats.totals.total_launches_30d}
                                        testId="stat-launches30"
                                    />
                                    <StatCard
                                        label="Active · 30d"
                                        value={stats.totals.active_users_30d}
                                        testId="stat-active30"
                                    />
                                    <StatCard
                                        label="Total Users"
                                        value={stats.totals.total_users}
                                        testId="stat-users"
                                    />
                                    <StatCard
                                        label="Total Apps"
                                        value={stats.totals.total_apps}
                                        testId="stat-apps"
                                    />
                                </div>

                                {/* Heatmap */}
                                <section className="bg-white brut-border brut-shadow p-5 sm:p-6">
                                    <h2 className="font-display text-xl mb-4 inline-flex items-center gap-2">
                                        <BarChart3 size={20} strokeWidth={3} />
                                        Last 30 Days
                                    </h2>
                                    <UsageHeatmap daily={stats.daily} />
                                </section>

                                <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
                                    {/* Top apps */}
                                    <section className="bg-white brut-border brut-shadow">
                                        <div className="bg-black text-white px-4 py-2 font-display inline-flex items-center gap-2 w-full">
                                            <TrendingUp
                                                size={16}
                                                strokeWidth={3}
                                            />
                                            Top Apps
                                        </div>
                                        <ul
                                            className="divide-y-[3px] divide-black"
                                            data-testid="top-apps-list"
                                        >
                                            {stats.top_apps.length === 0 && (
                                                <li className="p-6 font-display opacity-60">
                                                    No launches yet.
                                                </li>
                                            )}
                                            {stats.top_apps.map((a, idx) => {
                                                const max = Math.max(
                                                    1,
                                                    ...stats.top_apps.map(
                                                        (x) => x.launches,
                                                    ),
                                                );
                                                const pct = (a.launches / max) * 100;
                                                return (
                                                    <li
                                                        key={a.app_id}
                                                        className="relative px-4 py-3 flex items-center justify-between gap-3"
                                                        data-testid={`top-app-${a.app_id}`}
                                                    >
                                                        <div
                                                            className="absolute inset-y-0 left-0 bg-[#FFE2E0]"
                                                            style={{
                                                                width: `${pct}%`,
                                                            }}
                                                            aria-hidden="true"
                                                        />
                                                        <div className="relative flex items-center gap-3 min-w-0">
                                                            <span className="bg-black text-white w-7 h-7 flex items-center justify-center font-display text-xs brut-border shrink-0">
                                                                {idx + 1}
                                                            </span>
                                                            <span className="font-display text-sm truncate">
                                                                {a.name}
                                                            </span>
                                                        </div>
                                                        <span className="relative font-display text-sm bg-[#E8241A] text-white px-2 py-0.5 brut-border shrink-0">
                                                            {a.launches}
                                                        </span>
                                                    </li>
                                                );
                                            })}
                                        </ul>
                                    </section>

                                    {/* Ghost apps */}
                                    <section className="bg-white brut-border brut-shadow">
                                        <div className="bg-black text-white px-4 py-2 font-display inline-flex items-center gap-2 w-full">
                                            <Ghost
                                                size={16}
                                                strokeWidth={3}
                                            />
                                            Ghost Apps (idle ≥ 30d)
                                        </div>
                                        <ul
                                            className="divide-y-[3px] divide-black"
                                            data-testid="ghost-apps-list"
                                        >
                                            {stats.ghost_apps.length === 0 && (
                                                <li className="p-6 font-display opacity-60">
                                                    No idle apps — everything
                                                    got used this month. 🎯
                                                </li>
                                            )}
                                            {stats.ghost_apps.map((a) => (
                                                <li
                                                    key={a.app_id}
                                                    className="flex items-center justify-between gap-3 px-4 py-3"
                                                    data-testid={`ghost-app-${a.app_id}`}
                                                >
                                                    <div className="min-w-0">
                                                        <div className="font-display text-sm leading-tight truncate">
                                                            {a.name}
                                                        </div>
                                                        <div className="text-xs opacity-70 mt-0.5">
                                                            {a.last_launch
                                                                ? `Last used ${new Date(
                                                                      a.last_launch,
                                                                  ).toLocaleDateString()}`
                                                                : "Never launched"}
                                                        </div>
                                                    </div>
                                                    <button
                                                        onClick={async () => {
                                                            const appRow =
                                                                apps.find(
                                                                    (x) =>
                                                                        x.id ===
                                                                        a.app_id,
                                                                );
                                                            if (appRow)
                                                                await deleteApp(
                                                                    appRow,
                                                                );
                                                            await loadStats();
                                                        }}
                                                        className="px-3 py-2 brut-border bg-[#E8241A] text-white brut-press shrink-0"
                                                        data-testid={`ghost-app-del-${a.app_id}`}
                                                        aria-label={`Delete ${a.name}`}
                                                    >
                                                        <Trash2
                                                            size={14}
                                                            strokeWidth={3}
                                                        />
                                                    </button>
                                                </li>
                                            ))}
                                        </ul>
                                    </section>
                                </div>

                                <div className="text-center">
                                    <button
                                        type="button"
                                        onClick={loadStats}
                                        className="font-display text-xs tracking-widest underline"
                                        data-testid="stats-refresh"
                                    >
                                        Refresh data
                                    </button>
                                </div>
                            </>
                        )}
                    </div>
                )}
            </main>
        </div>
    );
}

function StatCard({ label, value, testId }) {
    return (
        <div
            className="bg-white brut-border brut-shadow-sm p-4"
            data-testid={testId}
        >
            <div className="font-display text-[11px] tracking-widest opacity-70">
                {label}
            </div>
            <div className="font-display text-3xl sm:text-4xl mt-1 leading-none">
                {value}
            </div>
        </div>
    );
}

function Field({ label, children }) {
    return (
        <label className="block">
            <span className="block font-display text-[11px] tracking-widest mb-1">
                {label}
            </span>
            {children}
        </label>
    );
}

function RadioPill({ checked, onClick, children, testId }) {
    return (
        <button
            type="button"
            onClick={onClick}
            className={[
                "flex-1 px-3 py-2 brut-border font-display text-xs",
                checked
                    ? "bg-black text-white"
                    : "bg-white text-black hover:bg-[#FAFAF7]",
            ].join(" ")}
            data-testid={testId}
        >
            {children}
        </button>
    );
}
